import { useMutation, useQuery } from '@tanstack/react-query'

import { useDispatch } from '@tabeeb/store'
import { useApi } from '@tabeeb/modules/shared/utils/API/hooks'

import { onAddErrorNotification, onAddInfoNotification } from '@tabeeb/modules/notification/actions'
import { formTitleFormatter } from '@tabeeb/modules/shared/utils/text'
import { TenantFormsQueryKeys } from '../queries'

const useTenantSncForm = ({ subdomain }: { subdomain: string }) => {
  const dispatch = useDispatch()

  const { mainApi: api } = useApi()

  const query = useQuery({
    queryFn: () => api.tenantForm.getTenantFormsBySubdomainAsync(subdomain),
    queryKey: [TenantFormsQueryKeys.TenantSncForm, subdomain],
  })

  const attachMutation = useMutation({
    mutationFn: api.tenantForm.attachFormToTenantAsync,
    onSuccess: () => {
      dispatch(
        onAddInfoNotification({
          message: formTitleFormatter.format(`Form was successfully attached to tenant`),
        })
      )
      query.refetch()
    },
    onError: () => {
      dispatch(onAddErrorNotification({ message: formTitleFormatter.format('Failed to attach form to tenant') }))
    },
  })

  const detachMutation = useMutation({
    mutationFn: (tenantFormId: number) => api.tenantForm.detachFormFromTenantAsync(tenantFormId),
    onSuccess: () => {
      dispatch(
        onAddInfoNotification({ message: formTitleFormatter.format(`Form was successfully detached from tenant`) })
      )
      query.refetch()
    },
    onError: () => {
      dispatch(onAddErrorNotification({ message: formTitleFormatter.format('Failed to detach form from tenant') }))
    },
  })

  return {
    fetching: query.isFetching,
    loading: query.isLoading,
    saving: attachMutation.isLoading || detachMutation.isLoading,
    forms: query.data,
    onAttach: attachMutation.mutate,
    onDetach: detachMutation.mutate,
    onReload: query.refetch,
  }
}

export default useTenantSncForm
